import { type GetDataIngestionSourcesResponseError } from '@autone/redux/data-ingestion';
import { useAutoneTranslation } from '@autone/translations';
import { isFetchBaseQueryError } from '@autone/utils/src/redux/errors';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { type SerializedError } from '@reduxjs/toolkit/react';
import { useState } from 'react';
import { z } from 'zod';

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../atoms';
import { AlertTriangleIcon, ArrowRightShortIcon } from '../../../../icons';
import { cn } from '../../../../utils';
import { SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES } from '../../constants/sidebarMenuButtonStyleOverrides';
import { DATA_FRESHNESS_BUTTON_ID } from '../const';
import { DataFreshnessWidgetTooltip } from '../DataFreshnessWidgetTooltip';

const sourcesNotFoundErrorSchema: z.ZodType<GetDataIngestionSourcesResponseError> =
  z.object({
    error_code: z.literal('SOURCES_NOT_FOUND'),
    detail: z.string(),
    body: z.null(),
  });

const isSourcesNotFoundError = (
  error: unknown,
): error is GetDataIngestionSourcesResponseError => {
  return !!sourcesNotFoundErrorSchema.safeParse(error);
};

export const DataFreshnessErrorPopover = ({
  error,
}: {
  error?: FetchBaseQueryError | SerializedError;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isSourcesNotFound =
    isFetchBaseQueryError(error) && isSourcesNotFoundError(error.data);

  const { common } = useAutoneTranslation();

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <DataFreshnessWidgetTooltip show={!isPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            id={DATA_FRESHNESS_BUTTON_ID}
            aria-label={common('data-age.global-widget.label')}
            variant="ghost"
            className={cn(SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES)}
          >
            <AlertTriangleIcon className="text-warning" />
            <div className="flex items-center gap-3 w-full group-data-[state=collapsed]:hidden">
              <div className="font-normal">
                {common('data-age.global-widget.label')}
              </div>
              <ArrowRightShortIcon className="ml-auto" />
            </div>
          </Button>
        </PopoverTrigger>
      </DataFreshnessWidgetTooltip>
      <PopoverContent
        className="dark w-fit max-w-64"
        side="right"
        align="start"
      >
        {isSourcesNotFound ? (
          <div>{common('data-age.global-widget.error.sources-not-found')}</div>
        ) : (
          <div>{common('data-age.global-widget.error.generic')}</div>
        )}
      </PopoverContent>
    </Popover>
  );
};
